import type { Condition, ResourceType } from '@repo/api-gw-sdk';
import { ConditionOperatorEnum } from '@repo/api-gw-sdk';

import { resourceId, resourceType } from '@/components/queryBuilder/properties';
import type { ResourcesScopeType } from '@/components/resourcesSelection/resourceSelectionDynamicViewer';

import { createCombineCondition, isAnAllCondition } from './advanceFilter';

interface BuildPolicyResourcesInventoryScopeOptions {
  resourceTypes?: ResourceType[];
  additionalScopedConditions?: Condition[];
}

function collectScopedConditions(
  options?: BuildPolicyResourcesInventoryScopeOptions
): Condition[] {
  const scoped: Condition[] = [];

  if (options?.resourceTypes?.length) {
    scoped.push({
      property: resourceType.name,
      operator: ConditionOperatorEnum.In,
      value: options.resourceTypes,
    });
  }

  if (options?.additionalScopedConditions?.length) {
    scoped.push(...options.additionalScopedConditions);
  }

  return scoped;
}

export function buildPolicyResourcesInventoryCondition(
  policyState: {
    condition?: Condition;
    include: string[];
    exclude: string[];
    resourcesScopeType: ResourcesScopeType;
  },
  options?: BuildPolicyResourcesInventoryScopeOptions
): Condition {
  const baseCondition = createCombineCondition(
    collectScopedConditions(options)
  );

  const excludeCondition: Condition = {
    operator: ConditionOperatorEnum.NotIn,
    property: resourceId.name,
    value: policyState.exclude,
  };

  const includeCondition: Condition = {
    operator: ConditionOperatorEnum.In,
    property: resourceId.name,
    value: policyState.include,
  };

  if (policyState.resourcesScopeType === 'allIncluded') {
    if (policyState.exclude?.length) {
      baseCondition.conditions!.push(excludeCondition);
    }
    return baseCondition;
  }

  if (policyState.resourcesScopeType === 'allExcluded') {
    baseCondition.conditions!.push(includeCondition);
    return baseCondition;
  }

  const userConditions =
    !policyState.condition || isAnAllCondition(policyState.condition)
      ? []
      : [policyState.condition];

  if (policyState.include?.length) {
    baseCondition.conditions!.push(
      userConditions.length
        ? createCombineCondition(
            [...userConditions, includeCondition],
            ConditionOperatorEnum.Or
          )
        : includeCondition
    );
  } else {
    baseCondition.conditions!.push(...userConditions);
  }

  if (policyState.exclude?.length) {
    baseCondition.conditions!.push(excludeCondition);
  }

  return baseCondition;
}
